import { faBrain, faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, List, Space, Tag, Typography, notification } from 'antd'
import { Memory, groupMemories, memoryDates } from '../../domain/memory/memoryModel'
import { useDeleteMemoryMutation, useMemoriesQuery } from '../../domain/memory/memoryQueries'

const warnDeleteFailed = () => notification.error({ message: "Couldn't delete that memory" })

// One mutation per row, so overlapping deletes keep their own spinner and error.
const MemoryRow = ({ memory }: { memory: Memory }) => {
  const deleteMemory = useDeleteMemoryMutation(warnDeleteFailed)
  const dates = memoryDates(memory)

  return (
    <List.Item
      actions={[
        <Button
          key="delete"
          type="text"
          danger
          aria-label={`Delete “${memory.content}”`}
          loading={deleteMemory.isLoading}
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => deleteMemory.mutate(memory.id)}
        />,
      ]}
    >
      <Space direction="vertical" size={0}>
        <Space>
          <Typography.Text>{memory.content}</Typography.Text>
          {memory.subject && <Tag>{memory.subject}</Tag>}
        </Space>
        <Typography.Text type="secondary">
          {dates.saved} · {dates.lastUsed}
        </Typography.Text>
      </Space>
    </List.Item>
  )
}

/**
 * The Account page's "What the AI Assistant remembers" section (#559).
 *
 * Shown only when `chat-memory` is on here *and* chat agrees: chat answers 404
 * while the flag is off for the caller, which covers a flag the API's
 * `/features` and chat evaluate differently. A failed load with the flag on
 * says so, rather than looking like the feature is off.
 */
const MemoryList = () => {
  const { data: memories, isError } = useMemoriesQuery()

  if (!isError && !memories) {
    return null
  }

  return (
    <section>
      <Typography.Title level={4}>
        <Space>
          <FontAwesomeIcon icon={faBrain} />
          What the AI Assistant remembers
        </Space>
      </Typography.Title>
      {isError || !memories ? (
        <Typography.Paragraph type="danger">
          Couldn&apos;t load your memories right now. Try again later.
        </Typography.Paragraph>
      ) : memories.length === 0 ? (
        <Typography.Paragraph type="secondary">
          Nothing yet. Tell the assistant “remember…” or “from now on…” and it will keep it for your
          future chats. Only you see what it remembers for you.
        </Typography.Paragraph>
      ) : (
        groupMemories(memories).map((group) => (
          <div key={group.category} role="region" aria-label={group.label}>
            <Typography.Title level={5}>{group.label}</Typography.Title>
            <List
              bordered
              dataSource={group.memories}
              renderItem={(memory) => <MemoryRow key={memory.id} memory={memory} />}
            />
          </div>
        ))
      )}
    </section>
  )
}

export default MemoryList
